import { Component } from "react";
import { Observable } from "rxjs";

// Type for API key that can be either a string or an object with token methods
export type ApiKeyOrGetter =
  | string
  | {
      getToken: () => Promise<string>;
      refreshToken: () => Promise<string>;
    };

export class GeneralService extends Component {
  public static readonly PROD_HOST = "api.trymaitai.ai";
  public static readonly PROD_WEST_HOST = "api.aws.us-west-2.trymaitai.ai";
  public static readonly PROD_ENDPOINT =
    process.env.REACT_APP_API_URL || `https://${GeneralService.PROD_HOST}`;
  public static readonly PROD_WEST_ENDPOINT =
    process.env.REACT_APP_API_URL_WEST ||
    `https://${GeneralService.PROD_WEST_HOST}`;
  public static readonly EVENTS_ENDPOINT =
    process.env.REACT_APP_EVENTS_URL || "https://events.trymaitai.ai";
  public static readonly options = {
    method: "GET",
    mode: "cors",
    headers: {
      "User-Agent": "Mozilla/5.0",
      "Content-Type": "application/json",
      "Accept-Charset": "UTF-8",
      "X-Device-Id": "",
      Authorization: "",
    },
  };

  static async handleResponse(response: Response, expectedType: any) {
    if (expectedType === JSON) {
      try {
        return await response.json();
      } catch (e) {
        console.error("Error getting JSON from response, getting TEXT instead");
        return await response.text();
      }
    } else if (expectedType === "blob") {
      return await response.blob();
    } else {
      return await response.text();
    }
  }

  static fetchHandler(
    path: string,
    options: any,
    loadingHandler: (val: boolean) => void,
    expectedType: any,
    endpoint: any,
    apiKey: ApiKeyOrGetter
  ): Observable<any> {
    return new Observable((observer: any) => {
      const fullPath = `${endpoint}${path}`;
      let cancelled = false;

      const makeRequest = async (
        tokenString: string,
        isRetry: boolean = false
      ) => {
        if (cancelled) {
          observer.complete();
          return;
        }

        // Check if token is a JWT token (has 2 dots) or a regular API key
        if (typeof apiKey === "object") {
          // It's a JWT token, use Authorization header
          options["headers"]["Authorization"] = `Bearer ${tokenString}`;
          delete options["headers"]["x-api-key"];
        } else {
          // It's a regular API key, use x-api-key header
          options["headers"]["x-api-key"] = tokenString;
          delete options["headers"]["Authorization"];
        }

        try {
          const response = await fetch(fullPath, options);

          // Do not return here when cancelled: we still need to consume the body and/or
          // complete the observable. Otherwise Strict Mode / remount leaves subscribers
          // without next/complete after the server already returned 200.

          if (response.ok) {
            const data = await this.handleResponse(response, expectedType);
            if (!cancelled) {
              observer.next(data);
              if (loadingHandler != null) loadingHandler(false);
            } else {
              // Unmounted (e.g. React Strict Mode): still finish the stream so UI does not hang.
              if (loadingHandler != null) loadingHandler(false);
            }
            observer.complete();
            return;
          }

          // Handle 401: refresh token and retry (but only once)
          if (
            response.status === 401 &&
            !isRetry &&
            typeof apiKey === "object"
          ) {
            try {
              console.log("Received 401, refreshing token and retrying...");
              const freshToken = await apiKey.refreshToken();
              if (!cancelled) {
                await makeRequest(freshToken, true); // Retry with fresh token
              } else {
                if (loadingHandler != null) loadingHandler(false);
                observer.complete();
              }
              return;
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              // Fall through to error handling
            }
          }

          // Handle other errors
          throw response;
        } catch (error) {
          if (cancelled) {
            observer.complete();
            return;
          }

          if (loadingHandler != null) loadingHandler(false);

          // If error is a Response object, try to extract message
          if (error instanceof Response) {
            try {
              const jsonError = await error.json();
              if (!cancelled) {
                if (jsonError && jsonError.message) {
                  observer.error(jsonError.message);
                } else {
                  observer.error(jsonError);
                }
              }
            } catch {
              try {
                const text = await error.text();
                if (!cancelled) {
                  observer.error(text);
                }
              } catch {
                if (!cancelled) {
                  observer.error(error);
                }
              }
            }
          } else {
            // Network error or other non-Response error
            if (!cancelled) {
              observer.error(error);
            }
          }
        }
      };

      // Start the async request
      (async () => {
        if (loadingHandler != null) loadingHandler(true);

        try {
          let tokenString: string;
          if (typeof apiKey === "string") {
            tokenString = apiKey;
          } else if (typeof apiKey === "object") {
            tokenString = await apiKey.getToken();
          } else {
            throw new Error("Invalid API key type");
          }

          if (!cancelled) {
            await makeRequest(tokenString, false);
          } else {
            if (loadingHandler != null) loadingHandler(false);
            observer.complete();
          }
        } catch (error) {
          if (!cancelled) {
            if (loadingHandler != null) loadingHandler(false);
            observer.error("Failed to get authentication token");
          } else if (loadingHandler != null) {
            loadingHandler(false);
            observer.complete();
          }
        }
      })();

      // Return teardown function
      return () => {
        cancelled = true;
      };
    });
  }

  public static get(
    path: string,
    loadingHandler: (val: boolean) => void = null,
    expectedType: any = JSON,
    endpoint: any = GeneralService.PROD_ENDPOINT,
    apiKey: ApiKeyOrGetter
  ) {
    const getOptions: any = structuredClone(GeneralService.options);
    return GeneralService.fetchHandler(
      path,
      getOptions,
      loadingHandler,
      expectedType,
      endpoint,
      apiKey
    );
  }

  public static getAsync(
    path: string,
    loadingHandler: (val: boolean) => void = null,
    expectedType: any = JSON,
    endpoint: any = GeneralService.PROD_ENDPOINT,
    apiKey: ApiKeyOrGetter
  ) {
    const getOptions: any = structuredClone(GeneralService.options);
    return GeneralService.fetchHandler(
      path,
      getOptions,
      loadingHandler,
      expectedType,
      endpoint,
      apiKey
    );
  }

  public static post(
    path: string,
    body: string | FormData,
    loadingHandler: (val: boolean) => void = null,
    expectedType: any = JSON,
    endpoint: any = GeneralService.PROD_ENDPOINT,
    apiKey: ApiKeyOrGetter,
    contentTypeOveride: any = null
  ) {
    const postOptions: any = structuredClone(GeneralService.options);
    postOptions.method = "POST";
    postOptions.body = body;
    if (contentTypeOveride) {
      delete postOptions.headers["Content-Type"];
    }
    return this.fetchHandler(
      path,
      postOptions,
      loadingHandler,
      expectedType,
      endpoint,
      apiKey
    );
  }

  public static put(
    path: string,
    body: string,
    loadingHandler: (val: boolean) => void = null,
    expectedType: any = JSON,
    endpoint: any = GeneralService.PROD_ENDPOINT,
    apiKey: ApiKeyOrGetter
  ) {
    const putOptions: any = structuredClone(GeneralService.options);
    putOptions.method = "PUT";
    putOptions.body = body;
    return this.fetchHandler(
      path,
      putOptions,
      loadingHandler,
      expectedType,
      endpoint,
      apiKey
    );
  }

  public static delete(
    path: string,
    body: string,
    loadingHandler: (val: boolean) => void = null,
    expectedType: any = JSON,
    endpoint: any = GeneralService.PROD_ENDPOINT,
    apiKey: ApiKeyOrGetter
  ) {
    const deleteOptions: any = structuredClone(GeneralService.options);
    deleteOptions.method = "DELETE";
    deleteOptions.body = body;
    return this.fetchHandler(
      path,
      deleteOptions,
      loadingHandler,
      expectedType,
      endpoint,
      apiKey
    );
  }
}

export default GeneralService;
